/* Shared primitives. Tokens only — no arbitrary values (spec section 12). */

.panel {
  background: var(--surface-raised);
  border: 1px solid var(--line-subtle);
  border-radius: var(--radius-lg);
  padding: var(--space-5);
}

.panelHeader {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-4);
  margin-bottom: var(--space-4);
  flex-wrap: wrap;
}

.sectionTitle {
  font-size: var(--text-md);
  letter-spacing: var(--tracking-wide);
  color: var(--ink-primary);
  font-weight: 600;
}

.hint {
  color: var(--ink-secondary);
  font-size: var(--text-sm);
  line-height: var(--leading-relaxed);
  max-width: var(--measure-prose);
}

/* ---- Buttons ---- */

.button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-4);
  border-radius: var(--radius-md);
  border: 1px solid var(--line-strong);
  background: var(--surface-inset);
  color: var(--ink-primary);
  font-size: var(--text-sm);
  font-weight: 500;
  min-height: 2.75rem;
  cursor: pointer;
  /* Keep foreground/background pairs atomic when a button changes variant.
     Interpolating only the background creates a brief low-contrast state. */
  transition: border-color var(--duration-fast) var(--ease);
}

.button:hover:not(:disabled) {
  background: var(--surface-hover);
  border-color: var(--accent-dim);
}

.button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.primary {
  background: var(--accent);
  border-color: var(--accent);
  color: var(--on-accent);
  font-weight: 600;
}

.primary:hover:not(:disabled) {
  background: var(--accent-strong);
  border-color: var(--accent-strong);
}

.ghost {
  background: none;
  border-color: transparent;
  color: var(--ink-secondary);
}

.ghost:hover:not(:disabled) {
  background: var(--surface-hover);
  color: var(--ink-primary);
}

.danger {
  border-color: var(--negative);
  color: var(--negative);
}

.danger:hover:not(:disabled) {
  background: var(--negative-wash);
  border-color: var(--negative);
}

.small {
  padding: var(--space-1) var(--space-3);
  font-size: var(--text-xs);
}

/* ---- Fields ---- */

/*
 * A control is sized by the value it holds, not by the container it sits in.
 * `--field-max` is the per-field escape hatch: set it inline (or from a
 * feature stylesheet) when a field genuinely needs the full column.
 */
.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  max-width: var(--field-max, var(--measure-field));
}

/* Numeric entry is narrower still: a four-digit weight in a 22rem box reads
   as an invitation to write an essay. */
.field:has(.numeric) {
  max-width: var(--field-max, var(--measure-field-narrow));
}

/* Opt-out for the rare field that should fill its column. */
.fieldWide {
  --field-max: 100%;
}

.label {
  font-size: var(--text-xs);
  color: var(--ink-secondary);
  font-weight: 500;
}

.required {
  color: var(--accent);
  margin-left: var(--space-1);
}

.control {
  background: var(--surface-inset);
  border: 1px solid var(--line-strong);
  border-radius: var(--radius-md);
  padding: var(--space-2) var(--space-3);
  color: var(--ink-primary);
  font-size: var(--text-sm);
  min-height: 2.75rem;
  width: 100%;
}

.control:hover:not(:disabled) {
  border-color: var(--line-strong);
}

.control:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.numeric {
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  text-align: right;
}

.invalid {
  border-color: var(--negative);
}

.description {
  font-size: var(--text-xs);
  color: var(--ink-secondary);
  line-height: var(--leading-relaxed);
  max-width: var(--measure-prose);
}

/* Errors pair an icon glyph with colour so they never rely on colour alone. */
.error {
  font-size: var(--text-xs);
  color: var(--negative);
  display: flex;
  align-items: flex-start;
  gap: var(--space-1);
}

.errorMark {
  font-weight: 700;
}

.suffix {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.suffixText {
  font-size: var(--text-xs);
  color: var(--ink-muted);
  white-space: nowrap;
}

/* ---- Choice pills ---- */

/*
 * A radio group wearing pills. The input stays in the DOM (and keeps its
 * keyboard behaviour) but is painted by the sibling face, so the control is
 * styleable without giving up native grouping or arrow-key navigation.
 */
.choiceField {
  border: 0;
  margin: 0;
  padding: 0;
  /* Fieldsets refuse to shrink below their content without this. */
  min-width: 0;
  max-width: var(--field-max, 100%);
}

.choiceField > .label {
  display: block;
  padding: 0;
  margin-bottom: var(--space-2);
}

.choices {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.choice {
  position: relative;
  display: inline-flex;
}

.choiceInput {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  opacity: 0;
  cursor: pointer;
}

.choiceFace {
  display: inline-flex;
  align-items: center;
  min-height: 2.75rem;
  padding: var(--space-2) var(--space-4);
  border: 1px solid var(--line-strong);
  border-radius: var(--radius-pill);
  background: var(--surface-inset);
  color: var(--ink-secondary);
  font-size: var(--text-sm);
  cursor: pointer;
}

.choiceInput:hover:not(:disabled) + .choiceFace {
  border-color: var(--accent-dim);
  color: var(--ink-primary);
}

/* Selection is carried by weight and border as well as colour. */
.choiceInput:checked + .choiceFace {
  border-color: var(--accent);
  background: var(--accent-wash);
  color: var(--accent-strong);
  font-weight: 600;
}

.choiceInput:focus-visible + .choiceFace {
  outline: var(--focus-ring);
  outline-offset: 2px;
}

.choiceField > .control,
.choiceField > .description,
.choiceField > .error {
  margin-top: var(--space-2);
}

/* ---- Empty states ---- */

/*
 * An empty state is a screen with nothing on it yet, not a hole in the page.
 * A dashed outline around open space reads as a missing asset; a mesh panel
 * reads as the instrument waiting for material.
 */
.empty {
  background-color: var(--surface-raised);
  background-image:
    repeating-linear-gradient(
      90deg,
      var(--mesh-line) 0 1px,
      transparent 1px var(--mesh-pitch)
    ),
    repeating-linear-gradient(
      0deg,
      var(--mesh-line) 0 1px,
      transparent 1px var(--mesh-pitch)
    );
  border: 1px solid var(--line-subtle);
  /* The accent edge marks this as the place to act, so the eye lands here
     rather than on the first panel that happens to have a border. */
  border-left: 3px solid var(--accent-dim);
  border-radius: var(--radius-lg);
  padding: var(--space-8);
  /* Capped and left-anchored. Three centred lines in a 1100px box read as a
     placeholder waiting for content; a capped block reads as the content. */
  max-width: 46rem;
  text-align: left;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-3);
}

.emptyTitle {
  font-family: var(--font-display);
  font-size: var(--text-xl);
  letter-spacing: var(--tracking-wide);
  font-weight: 600;
}

.emptyBody {
  color: var(--ink-secondary);
  font-size: var(--text-sm);
  max-width: 58ch;
  line-height: var(--leading-relaxed);
}

/* ---- Dialog ---- */

.dialog {
  border: 1px solid var(--line-strong);
  border-radius: var(--radius-lg);
  background: var(--surface-overlay);
  color: var(--ink-primary);
  padding: 0;
  max-width: min(34rem, calc(100vw - var(--space-8)));
  width: 100%;
}

/* Wide enough for a form and a live preview side by side. */
.dialogWide {
  max-width: min(56rem, calc(100vw - var(--space-8)));
}

.dialog::backdrop {
  background: rgb(0 0 0 / 65%);
}

.dialogInner {
  padding: var(--space-5);
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.dialogTitle {
  font-size: var(--text-lg);
}

.dialogActions {
  display: flex;
  gap: var(--space-2);
  justify-content: flex-end;
  flex-wrap: wrap;
}

/* ---- Expandable images ---- */

.imageTrigger {
  position: relative;
  display: block;
  width: fit-content;
  padding: 0;
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--ink-primary);
  line-height: 0;
  cursor: zoom-in;
}

.imageTrigger:focus-visible {
  outline: var(--focus-ring);
  outline-offset: var(--focus-offset);
}

.imageHint {
  position: absolute;
  right: var(--space-1);
  bottom: var(--space-1);
  padding: var(--space-1) var(--space-2);
  border: 1px solid var(--line-strong);
  background: var(--surface-overlay);
  color: var(--ink-primary);
  font-family: var(--font-display);
  font-size: var(--text-2xs);
  line-height: var(--leading-tight);
  letter-spacing: var(--tracking-wide);
  text-transform: uppercase;
}

.expandedImage {
  display: block;
  width: auto;
  max-width: 100%;
  max-height: 72dvh;
  margin: auto;
  object-fit: contain;
}

/* ---- Badges ---- */

/* Every badge pairs its colour with a text label, never colour alone. */
.badge {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-pill);
  border: 1px solid var(--line-strong);
  font-size: var(--text-2xs);
  letter-spacing: 0.04em;
  color: var(--ink-secondary);
  white-space: nowrap;
}

.badgeBaseline {
  border-color: var(--evidence-baseline);
  color: var(--evidence-baseline);
}

.badgeEmerging {
  border-color: var(--evidence-emerging);
  color: var(--evidence-emerging);
}

.badgeDeveloping {
  border-color: var(--evidence-developing);
  color: var(--evidence-developing);
}

.badgeCalibrated {
  border-color: var(--evidence-calibrated);
  color: var(--evidence-calibrated);
}

.badgeWarning {
  border-color: var(--warning);
  color: var(--warning);
  background: var(--warning-wash);
}

/* ---- Lists ---- */

.stack {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.row {
  display: flex;
  gap: var(--space-3);
  align-items: flex-end;
  flex-wrap: wrap;
}

.grid2 {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(13rem, 1fr));
  gap: var(--space-4);
}

.listItem {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-4);
  padding: var(--space-3) var(--space-4);
  border: 1px solid var(--line-subtle);
  border-radius: var(--radius-md);
  background: var(--surface-inset);
  flex-wrap: wrap;
}

.listItemMain {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.listItemTitle {
  font-weight: 600;
}

.listItemMeta {
  font-size: var(--text-xs);
  color: var(--ink-muted);
}

.listItemActions {
  display: flex;
  gap: var(--space-2);
  flex-shrink: 0;
}

/* ---- Notices ---- */

.notice {
  border: 1px solid var(--line-strong);
  border-left-width: 3px;
  border-radius: var(--radius-md);
  padding: var(--space-3) var(--space-4);
  font-size: var(--text-sm);
  line-height: var(--leading-relaxed);
  color: var(--ink-secondary);
  /* A notice is always prose, so it is capped at a readable measure rather
     than run to the full width of the panel. */
  max-width: var(--measure-prose);
}

.noticeWarning {
  border-left-color: var(--warning);
  background: var(--warning-wash);
}

.noticeInfo {
  border-left-color: var(--info);
}

.noticeDanger {
  border-left-color: var(--negative);
  background: var(--negative-wash);
}

/* Reserved for the moment a flow completes, so "done" never looks like
   "information". */
.noticeSuccess {
  border-left-color: var(--positive);
  background: var(--positive-wash);
}

/* ---- Auth gate ---- */

.authShell {
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-6);
}

.authLayout {
  width: min(100%, 64rem);
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(22rem, 26rem);
  gap: var(--space-16);
  align-items: center;
}

.authIntro {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-4);
  max-width: 34rem;
}

.authEyebrow {
  font-family: var(--font-display);
  font-size: var(--text-xs);
  letter-spacing: var(--tracking-caps);
  text-transform: uppercase;
  color: var(--accent);
}

.authLead {
  font-family: var(--font-display);
  font-size: var(--text-2xl);
  line-height: var(--leading-tight);
  letter-spacing: var(--tracking-wide);
  color: var(--ink-primary);
}

.authSummary {
  max-width: 54ch;
  color: var(--ink-secondary);
  line-height: var(--leading-relaxed);
}

.authBenefits {
  display: grid;
  gap: var(--space-3);
  list-style: none;
  padding-top: var(--space-4);
  border-top: 1px solid var(--line-strong);
  color: var(--ink-secondary);
}

.authBenefits li {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: var(--space-3);
  align-items: baseline;
}

.authBenefits li::before {
  content: '—';
  color: var(--accent-dim);
}

.authCard {
  width: 100%;
  max-width: 26rem;
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.authCardHeader {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.authCardTitle {
  font-size: var(--text-xl);
}

.authMode {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-2);
}

.authMode > button,
.authPrimaryAction,
.authSecondaryAction {
  width: 100%;
}

.authForm {
  margin-top: var(--space-5);
}

.authDivider {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  color: var(--ink-muted);
  font-size: var(--text-xs);
  text-transform: uppercase;
  letter-spacing: var(--tracking-caps);
}

.authDivider::before,
.authDivider::after {
  content: '';
  flex: 1;
  border-top: 1px solid var(--line-subtle);
}

.authTertiary {
  align-self: center;
}

/*
 * The sign-in gate is the one screen with room for the full lockup, so the
 * brand is shown as drawn rather than set in type. The heading keeps its
 * accessible name from the image's alt text.
 */
.authBrand {
  display: block;
  margin: calc(var(--space-1) * -1) 0;
}

.authLockup {
  display: block;
  width: min(100%, 20rem);
  height: auto;
}

@media (max-width: 800px) {
  .authShell {
    align-items: flex-start;
    padding: var(--space-8) var(--space-4);
  }

  .authLayout {
    grid-template-columns: minmax(0, 1fr);
    gap: var(--space-8);
    max-width: 32rem;
  }

  .authIntro {
    gap: var(--space-3);
  }

  .authLockup {
    width: min(100%, 15rem);
  }
}
